import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Space,
  Typography,
  Modal,
  Form,
  Switch,
  Tag,
  message,
  Popconfirm,
  Tooltip,
} from "antd";
import { Search, Plus, Edit, Trash2 } from "lucide-react";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const AttributesIndex = () => {
  const { request } = useRequest();
  const [form] = Form.useForm();
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [editingRecord, setEditingRecord] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  // Normalize attribute data from API
  const normalizeAttribute = (item, index) => ({
    key: item?.id ?? index,
    id: item?.id,
    srNo: index + 1,
    name: item?.name ?? "",
    status: item?.status !== undefined ? item.status : true,
    createdAt: item?.created_at || item?.createdAt || "",
  });

  // Fetch all attributes
  const fetchAttributes = useCallback(() => {
    setLoading(true);
    request({
      url: apiPath.listAttribute,
      method: "GET",
      onSuccess: (response) => {
        const data = response?.data || response?.results || [];
        const normalizedData = (Array.isArray(data) ? data : []).map(normalizeAttribute);
        setAttributes(normalizedData);
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast("Failed to fetch attributes", Severty.ERROR);
      },
    });
  }, [request]);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  const resetModalState = () => {
    setEditingRecord(null);
    setDetailLoading(false);
    setModalLoading(false);
    form.resetFields();
  };

  const openCreateModal = () => {
    resetModalState();
    form.setFieldsValue({ status: true });
    setModalOpen(true);
  };

  const openEditModal = async (record) => {
    resetModalState();
    setEditingRecord(record);
    setDetailLoading(true);
    setModalOpen(true);

    // Since we already have the data in the table, just populate the form
    form.setFieldsValue({
      name: record.name,
      status: record.status,
    });
    setDetailLoading(false);
  };

  // Toggle attribute status
  const toggleAttributeStatus = async (record) => {
    Modal.confirm({
      title: `Confirm Status Change`,
      content: `Are you sure you want to ${record.status ? 'deactivate' : 'activate'} attribute "${record.name}"?`,
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        setUpdatingStatusId(record.id);
        try {
          const apiUrl = apiPath.statusUpdateAttribute(record.id);
          console.log("Calling API:", apiUrl); // For debugging
          
          request({
            url: apiUrl,
            method: "PATCH",
            data: { status: !record.status },
            onSuccess: (response) => {
              setUpdatingStatusId(null);
              ShowToast(
                response?.data?.message || `Attribute ${!record.status ? 'activated' : 'deactivated'} successfully`,
                Severty.SUCCESS
              );
              fetchAttributes(); // Refresh the list
            },
            onError: (error) => {
              setUpdatingStatusId(null);
              ShowToast(
                error?.response?.data?.message || "Failed to update attribute status",
                Severty.ERROR
              );
            },
          });
        } catch (error) {
          setUpdatingStatusId(null);
          ShowToast("Failed to update attribute status", Severty.ERROR);
        }
      },
    });
  };

  const handleDelete = async (record) => {
    Modal.confirm({
      title: "Delete Attribute",
      content: `Are you sure you want to delete attribute "${record.name}"?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setLoading(true);
        request({
          url: apiPath.deleteAttribute(record.id),
          method: "DELETE",
          onSuccess: (response) => {
            setLoading(false);
            ShowToast("Attribute deleted successfully", Severty.SUCCESS);
            fetchAttributes();
          },
          onError: (error) => {
            setLoading(false);
            ShowToast(error?.response?.data?.message || "Failed to delete attribute", Severty.ERROR);
          },
        });
      },
    });
  };

  const handleSubmit = async (values) => {
    setModalLoading(true);

    if (editingRecord) {
      // Update attribute
      request({
        url: apiPath.updateAttribute(editingRecord.id),
        method: "PUT",
        data: { 
          name: values.name,
          status: values.status // Include status in update
        },
        onSuccess: (response) => {
          setModalLoading(false);
          ShowToast("Attribute updated successfully", Severty.SUCCESS);
          setModalOpen(false);
          resetModalState();
          fetchAttributes();
        },
        onError: (error) => {
          setModalLoading(false);
          ShowToast(error?.response?.data?.message || "Failed to update attribute", Severty.ERROR);
        },
      });
    } else {
      // Create new attribute
      request({
        url: apiPath.addAttribute,
        method: "POST",
        data: { 
          name: values.name,
          status: values.status // Include status when creating
        },
        onSuccess: (response) => {
          setModalLoading(false);
          ShowToast("Attribute created successfully", Severty.SUCCESS);
          setModalOpen(false);
          resetModalState();
          fetchAttributes();
        },
        onError: (error) => {
          setModalLoading(false);
          ShowToast(error?.response?.data?.message || "Failed to create attribute", Severty.ERROR);
        },
      });
    }
  };

  // Filter attributes based on search
  const filteredAttributes = attributes.filter((item) => {
    const query = searchText.trim().toLowerCase();
    if (!query) return true;
    return item.name.toLowerCase().includes(query);
  });

  const columns = [
    {
      title: "SR No.",
      dataIndex: "srNo",
      key: "srNo",
      width: 80,
    },
    {
      title: "Attribute Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status, record) => (
        <Space direction="vertical" size={4}>
          {/* <Tag color={status ? "green" : "orange"}>
            {status ? "Active" : "Inactive"}
          </Tag> */}
          <Tooltip title={status ? "Click to deactivate" : "Click to activate"}>
            <Switch
              checked={status}
              onChange={() => toggleAttributeStatus(record)}
              checkedChildren="Active"
              unCheckedChildren="Inactive"
              loading={updatingStatusId === record.id}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => date ? dayjs(date).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      width: 100,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit Attribute">
            <Button
              type="text"
              icon={<Edit size={16} style={{ color: "#10b981" }} />}
              onClick={() => openEditModal(record)} 
            />
          </Tooltip>
          <Tooltip title="Delete Attribute">
            <Button
              type="text"
              danger
              icon={<Trash2 size={16} style={{ color: "#ef4444" }} />}
              onClick={() => handleDelete(record)} 
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Statistics
  const activeCount = attributes.filter(attr => attr.status).length;
  const inactiveCount = attributes.filter(attr => !attr.status).length;

  return (
    <div className="attributes-container" style={{ padding: '24px' , background:'#fff' , borderRadius:"10px" }}>
      <div className="attributes-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        <Title level={3} style={{ margin: 0 }}>Product Attributes</Title>
        <Button type="primary" icon={<Plus size={16} />} onClick={openCreateModal}>
          Add Attribute
        </Button>
      </div>

      {/* Statistics Cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: 16,
        marginBottom: 20
      }}>
        <div style={{ background: "#fff", padding: "16px", borderRadius: 8, textAlign: "center", border: "1px solid #f0f0f0" }}>
          <Text type="secondary">Total Attributes</Text>
          <div style={{ fontSize: 28, fontWeight: 600, color: "#1890ff" }}>
            {attributes.length}
          </div>
        </div>
        <div style={{ background: "#fff", padding: "16px", borderRadius: 8, textAlign: "center", border: "1px solid #f0f0f0" }}>
          <Text type="secondary">Active Attributes</Text>
          <div style={{ fontSize: 28, fontWeight: 600, color: "#52c41a" }}>
            {activeCount}
          </div>
        </div>
        <div style={{ background: "#fff", padding: "16px", borderRadius: 8, textAlign: "center", border: "1px solid #f0f0f0" }}>
          <Text type="secondary">Inactive Attributes</Text>
          <div style={{ fontSize: 28, fontWeight: 600, color: "#ff4d4f" }}>
            {inactiveCount}
          </div>
        </div>
      </div>

      <div className="attributes-search-section" style={{ marginBottom: '16px' }}>
        <Input
          placeholder="Search attributes..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          suffix={<Search size={16} color="#94a3b8" />}
          style={{ width: 300 }}
          allowClear
        />
      </div>

      <Table
        columns={columns}
        dataSource={filteredAttributes}
        loading={loading}
        pagination={{ pageSize: 10, showTotal: (total) => `Total ${total} attributes` }}
        rowKey="key"
        bordered
      />

      <Modal
        title={editingRecord ? "Edit Attribute" : "Add New Attribute"}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          resetModalState();
        }}
        footer={null}
        destroyOnClose
        width={500}
      >
        {detailLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>Loading...</div>
        ) : (
          <Form 
            form={form} 
            layout="vertical" 
            onFinish={handleSubmit}
            initialValues={{ status: true }}
          >
            <Form.Item
              label="Attribute Name"
              name="name"
              rules={[
                { required: true, message: "Please enter attribute name" },
                { min: 2, message: "Attribute name must be at least 2 characters" },
                { max: 50, message: "Attribute name cannot exceed 50 characters" }
              ]}
            >
              <Input 
                placeholder="e.g. Color, Size, Material" 
                autoFocus
                disabled={modalLoading}
              />
            </Form.Item>

            {/* <Form.Item
              label="Status"
              name="status"
              valuePropName="checked"
              tooltip="Set the attribute as active or inactive"
            >
              <Switch 
                checkedChildren="Active" 
                unCheckedChildren="Inactive" 
                disabled={modalLoading}
              />
            </Form.Item> */}

            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button onClick={() => {
                  setModalOpen(false);
                  resetModalState();
                }} disabled={modalLoading}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={modalLoading}>
                  {editingRecord ? "Update" : "Create"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default AttributesIndex;